import {
  AccordionItem,
  Badge,
  boxShadow,
  Inline,
  paletteHelper,
  radiusHelper,
  spacingHelper,
  typographyHelper,
} from '@property.com.au/ui-kit';
import type { FunctionComponent, PropsWithChildren, ReactNode } from 'react';
import styled from 'styled-components';

const HeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: ${spacingHelper('twoExtraSmall')} 0;
`;

const Title = styled.span`
  ${typographyHelper('subtitle02')};
`;

const StyledAccordionItem = styled(AccordionItem)`
  background-color: ${paletteHelper('backgroundPrimaryHigh')};
  border: none;
  border-radius: ${radiusHelper('medium')};
  ${boxShadow.defaultBoxShadow}
  margin-bottom: ${spacingHelper('small')};

  .filterHeaderButton {
    border-radius: ${radiusHelper('medium')};
    /* override rules so that button color doesn't change on hover */
    &:hover {
      background-color: initial;
    }
    &:focus-visible {
      background-color: transparent;
      outline: solid calc(${spacingHelper('twoExtraSmall')} / 2);
    }
    pointer-events: none;
  }

  .filterContent {
    padding-top: 0;
  }

  .filterChevronWrapper {
    display: none;
  }

  &:last-of-type {
    /* override accordion rule that last item has a bottom border */
    border-bottom: none;
  }
`;

const HeaderBadge = styled(Badge)`
  gap: ${spacingHelper('twoExtraSmall')};

  /*
   * Bicycle CK Icon doesn't have an MD variant (BicycleMd)
   * Use the large variant instead and force the dimensions
  */

  .CK__Icon--large {
    height: ${spacingHelper('medium')};
    width: ${spacingHelper('medium')};
  }
`;

interface FilterAccordionItemProps {
  icon: ReactNode;
  title: string;
  badgeText: string;
  badgeIcon?: ReactNode;
  onToggle?: () => void;
  expanded?: boolean;
  className?: string;
}

export const FilterAccordionItem: FunctionComponent<
  PropsWithChildren<FilterAccordionItemProps>
> = ({ children, title, badgeText, badgeIcon, icon, onToggle, expanded, className }) => {
  return (
    <StyledAccordionItem
      onToggle={onToggle}
      expanded={expanded}
      id={title.toLowerCase()}
      title={
        <HeaderWrapper>
          <Inline grow={false} gap="small">
            {icon}
            <Title>{title}</Title>
          </Inline>
          <HeaderBadge variant="grey">
            {badgeIcon}
            {badgeText}
          </HeaderBadge>
        </HeaderWrapper>
      }
      headingLevel={0}
      dangerouslySetClassNames={{
        header: 'filterHeader',
        headerButton: 'filterHeaderButton',
        content: 'filterContent',
        chevronWrapper: 'filterChevronWrapper',
      }}
      className={className}
    >
      {children}
    </StyledAccordionItem>
  );
};
